import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from "@material-ui/core";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import CircularProgress from "@material-ui/core/CircularProgress";
import "./dataTables.css";

export default function DataTables({
  data,
  headers,
  loading,
  paginate = true,
  lastPage,
  setLastPage,
}) {
  const [orderBy, setOrderBy] = useState({ col: null, asc: true });
  const [rowsPerPage, setRowsPerPage] = useState(data.length);
  const [pageActual, setPageActual] = useState(lastPage || 0);
  const [resize, setResize] = useState(false);
  const tableRef = useRef(null);

  const sortedData = useMemo(() => {
    if (orderBy.col === null) return [...data];
    return [...data].sort((a, b) => {
      if (a[orderBy.col] > b[orderBy.col]) return orderBy.asc ? 1 : -1;
      if (a[orderBy.col] < b[orderBy.col]) return orderBy.asc ? -1 : 1;
      return 0;
    });
  }, [data, orderBy]);

  useEffect(() => {
    if (paginate && tableRef.current) {
      const height = tableRef.current.clientHeight;
      const newRows = Math.trunc(height / 35);
      setRowsPerPage(newRows);
    }
  }, [sortedData, paginate, resize]);

  useEffect(() => {
    const handleResize = () => setResize((prev) => !prev);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function getRows() {
    const rows = [];
    const startIndex = rowsPerPage * pageActual;
    const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);

    for (let i = startIndex; i < endIndex; i++) {
      rows.push(
        <TableRow key={i}>
          {sortedData[i].map((cell, index) => (
            <TableCell key={index}>{cell}</TableCell>
          ))}
        </TableRow>,
      );
    }

    return rows.length > 0 ? rows : null;
  }

  function getPaginate() {
    const totalPages = Math.ceil(sortedData.length / rowsPerPage);
    const buttons = [];

    const goToPage = (i) => {
      setPageActual(i);
      if (setLastPage) setLastPage(i);
    };

    buttons.push(
      <button
        key="Anterior"
        onClick={() => pageActual > 0 && goToPage(pageActual - 1)}
      >
        Anterior
      </button>,
    );

    if (totalPages > 6) {
      if (pageActual <= 2) {
        for (let i = 0; i < 3; i++) {
          buttons.push(
            <button
              key={i + 1}
              onClick={() => goToPage(i)}
              className={pageActual === i ? "active" : null}
            >
              {i + 1}
            </button>,
          );
        }
        buttons.push(<button key="dots1">...</button>);
      } else if (pageActual >= totalPages - 3) {
        buttons.push(<button key="dots2">...</button>);
        for (let i = totalPages - 3; i < totalPages; i++) {
          buttons.push(
            <button
              key={i + 1}
              onClick={() => goToPage(i)}
              className={pageActual === i ? "active" : null}
            >
              {i + 1}
            </button>,
          );
        }
      } else {
        buttons.push(<button key="dots3">...</button>);
        for (let i = pageActual - 2; i < pageActual + 3; i++) {
          buttons.push(
            <button
              key={i + 1}
              onClick={() => goToPage(i)}
              className={pageActual === i ? "active" : null}
            >
              {i + 1}
            </button>,
          );
        }
        buttons.push(<button key="dots4">...</button>);
      }
    } else {
      for (let i = 0; i < totalPages; i++) {
        buttons.push(
          <button
            key={i + 1}
            onClick={() => goToPage(i)}
            className={pageActual === i ? "active" : null}
          >
            {i + 1}
          </button>,
        );
      }
    }

    buttons.push(
      <button
        key="Siguiente"
        onClick={() => pageActual < totalPages - 1 && goToPage(pageActual + 1)}
      >
        Siguiente
      </button>,
    );

    return <div className="contenedor-buttons">{buttons}</div>;
  }

  return (
    <>
      <div
        ref={tableRef}
        style={
          paginate
            ? {
                height: "calc(100% - 60px)",
                overflow: "hidden",
                position: "relative",
              }
            : { height: "100%", overflow: "auto", position: "relative" }
        }
      >
        <Table stickyHeader className="table-dash">
          <TableHead>
            <TableRow>
              {headers.map((header, index) => (
                <TableCell
                  key={header}
                  className="HeadCell"
                  onClick={() => {
                    if (index === orderBy.col)
                      setOrderBy({ ...orderBy, asc: !orderBy.asc });
                    else setOrderBy({ col: index, asc: true });
                  }}
                >
                  {header}
                  <div className="HeadArrows">
                    <ArrowDropUpIcon
                      style={
                        orderBy.col === index && orderBy.asc
                          ? { color: "red" }
                          : null
                      }
                    />
                    <ArrowDropDownIcon
                      style={
                        orderBy.col === index && !orderBy.asc
                          ? { color: "red" }
                          : null
                      }
                    />
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {loading ? null : <TableBody>{getRows()}</TableBody>}
        </Table>
        {loading && (
          <div
            style={{
              textAlign: "center",
              marginTop: "10px",
              position: "absolute",
              width: "100%",
            }}
          >
            <CircularProgress size={50} />
          </div>
        )}
        {!loading && sortedData.length === 0 && (
          <p
            style={{ textAlign: "center", width: "100%", position: "absolute" }}
          >
            No hay registros que mostrar.
          </p>
        )}
      </div>
      {paginate && getPaginate()}
    </>
  );
}
